/**
 * useConnectActivity — owner-side access-log feed for a Connect Space.
 *
 * Reads ``GET /connect/spaces/{id}/activity`` (audit fields only — no cost/EUR).
 * An empty feed renders the honest empty-state; a real error (403 non-owner,
 * 5xx) flows through ``isError``.
 *
 * Polls at 30s — the same cadence as the rest of the Connect detail surfaces.
 */

import { type ConnectActivityEntry, getSpaceActivity } from "@ando/core";
import { useQuery } from "@tanstack/react-query";

import { connectQueryKey } from "./useConnectSpaces.js";

export interface UseConnectActivityResult {
  readonly entries: readonly ConnectActivityEntry[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  readonly refetch: () => Promise<unknown>;
}

export function connectActivityQueryKey(spaceId: string): readonly unknown[] {
  return [...connectQueryKey.detail(spaceId), "activity"] as const;
}

export function useConnectActivity(
  spaceId: string,
  options: { enabled?: boolean } = {},
): UseConnectActivityResult {
  const enabled = options.enabled ?? true;
  const query = useQuery({
    queryKey: connectActivityQueryKey(spaceId),
    queryFn: () => getSpaceActivity(spaceId),
    enabled,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  return {
    entries: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => query.refetch(),
  };
}
